import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { V1OAuthApp } from '@/generated/hooks/gringotts/models';
import {
  getListOAuthAppsQueryKey,
  useCreateOAuthApp,
  useCreateOAuthApp2,
} from '@/generated/hooks/gringotts/v1/services/oauth_app_service';

import { ECredentialScope } from '../types';

/** Arguments for {@link useRegisterOAuthApp}'s `registerOAuthApp`. */
export interface IRegisterOAuthAppArgs {
  scope: ECredentialScope;
  /** The app to register: name, secret type, config and client credentials. */
  app: V1OAuthApp;
}

/**
 * Registers an OAuth app in the workspace or the organization, so credentials
 * of its secret type can sign in through it.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns `registerOAuthApp` (resolves to the new app) and its pending state.
 */
export function useRegisterOAuthApp(
  organizationId: string,
  workspaceId: string,
) {
  const queryClient = useQueryClient();
  const createOrgApp = useCreateOAuthApp();
  const createWorkspaceApp = useCreateOAuthApp2();

  const registerOAuthApp = useCallback(
    async ({ scope, app }: IRegisterOAuthAppArgs): Promise<V1OAuthApp> => {
      const data = { oauth_app: app };
      const created =
        scope === ECredentialScope.Workspace
          ? await createWorkspaceApp.mutateAsync({
              organizationId,
              workspaceId,
              data,
            })
          : await createOrgApp.mutateAsync({ organizationId, data });
      void queryClient.invalidateQueries({
        queryKey: getListOAuthAppsQueryKey(organizationId),
      });
      return created.data;
    },
    [
      organizationId,
      workspaceId,
      queryClient,
      createOrgApp,
      createWorkspaceApp,
    ],
  );

  return {
    registerOAuthApp,
    isPending: createOrgApp.isPending || createWorkspaceApp.isPending,
  };
}
